/* eslint-disable max-lines */
import { clearURLParams, exportUsers, fetchUsersSeats } from 'actions';
import { selectUsers } from 'actions/userPermissionsPage';
import UserInactiveFilter from 'components/Filters/UserInactiveFilter';
import UserIntegrationFilter from 'components/Filters/UserIntegrationFilter';
import UserRoleFilter from 'components/Filters/UserRolesFilter';
import UserTeamsFilter from 'components/Filters/UserTeamsFilter';
import LoadingIndicator from 'components/LoadingIndicator';
import Permissions from 'components/Permissions';
import SearchInput from 'components/SearchInput';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getCustomerDomain } from 'selectors/auth';
import { getFetchUsersSeatsLoading, getUsersSeats } from 'selectors/users';
import { useSelector } from 'store';
import { useHistory } from 'utils/urls';

const UsersListHeader: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const domain = useSelector(getCustomerDomain);
  const userSeats = useSelector(getUsersSeats);
  const seatsLoading = useSelector(getFetchUsersSeatsLoading);

  const remainingInsight =
    userSeats.insight_total - userSeats.insight > 0
      ? userSeats.insight_total - userSeats.insight
      : 0;
  const remainingPrevent =
    userSeats.prevent_total - userSeats.prevent > 0
      ? userSeats.prevent_total - userSeats.prevent
      : 0;

  useEffect(() => {
    dispatch(fetchUsersSeats());
  }, [dispatch]);

  const handleExportUsers = (): void => {
    dispatch(exportUsers);
  };

  const handleClearFilters = (): void => {
    dispatch(clearURLParams());
  };

  return (
    <header className="flex flex-col gap-6">
      <div className="flex flex-row justify-between">
        <div className="w-full flex flex-col gap-4 justify-between">
          <h2 className="title w-22 flex-shrink-0">Users</h2>
          <div className="flex flex-row justify-start gap-2">
            <button
              onClick={handleExportUsers}
              type="button"
              className="h-8 button button--secondary focus:outline-none"
              key="export"
            >
              <span className="font-bold whitespace-no-wrap">Download CSV</span>
            </button>
            <Permissions action="customers.list">
              <button
                type="button"
                className="w-25 h-8 flex flex-row justify-center button button--primary focus:outline-none whitespace-no-wrap"
                key="add"
                onClick={(): void => {
                  dispatch(selectUsers([]));
                  history.pushLookup({ routeName: 'users-permissions', customerDomain: domain });
                }}
              >
                Add New
              </button>
            </Permissions>
          </div>
        </div>

        <div className="flex flex-col items-end self-end justify-between text-body">
          <div className="mb-0.5 flex flex-row items-center text-litlingo-gray-5 font-bold">
            <span className="w-18 text-center">Total</span>
            <span className="w-25 text-center">Remaining</span>
          </div>

          <div className="flex flex-row">
            <div className="mr-2.5 h-16 flex flex-col justify-around items-end text-litlingo-gray-5 font-bold">
              <span className="">Insight</span>
              <span className="whitespace-no-wrap truncate">Prevent</span>
            </div>

            {seatsLoading ? (
              <div className="w-43 flex justify-center items-center border border-litlingo-gray-3 rounded">
                <LoadingIndicator size="10" />
              </div>
            ) : (
              <>
                <div className="flex flex-col items-center text-litlingo-gray-5 font-bold">
                  <div className="h-8 w-18 px-2.5 py-1.5 items-center text-center border-l border-t border-litlingo-gray-3 rounded-tl focus:outline-none">
                    {userSeats.insight_total || 0}
                  </div>
                  <div className="h-8 w-18 px-2.5 py-1.5 items-center text-center border-l border-b border-t border-litlingo-gray-3 rounded-bl focus:outline-none">
                    {userSeats.prevent_total || 0}
                  </div>
                </div>
                <div className="flex flex-col items-center text-litlingo-gray-5 font-bold">
                  <div
                    className={`h-8 w-25 px-2.5 py-1.5 items-center text-center border-r border-l border-t border-litlingo-gray-3 rounded-tr focus:outline-none ${
                      remainingInsight <= 0 ? 'text-litlingo-alert' : ''
                    }`}
                  >
                    {remainingInsight}
                  </div>
                  <div
                    className={`h-8 w-25 px-2.5 py-1.5 items-center text-center border border-litlingo-gray-3 rounded-br focus:outline-none ${
                      remainingPrevent <= 0 ? 'text-litlingo-alert' : ''
                    }`}
                  >
                    {remainingPrevent}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-row justify-between">
        <div className="flex flex-row gap-2 flex-wrap">
          <SearchInput
            resource={resourceQueryParamName.user}
            field="broad_search"
            className="h-8 max-w-68"
            placeholder="Search users by name or email"
          />

          <UserRoleFilter resource={resourceQueryParamName.user} />

          <div className="w-26">
            <UserTeamsFilter kind="division" resource={resourceQueryParamName.user} />
          </div>
          <div className="w-26">
            <UserTeamsFilter kind="region" resource={resourceQueryParamName.user} />
          </div>
          <div className="w-26">
            <UserTeamsFilter kind="management-level" resource={resourceQueryParamName.user} />
          </div>

          <UserIntegrationFilter resource={resourceQueryParamName.user} />
          <UserInactiveFilter resource={resourceQueryParamName.user} />
          <button
            type="button"
            className="text-body self-end focus:outline-none"
            onClick={handleClearFilters}
          >
            <span className="text-litlingo-primary underline">Clear All</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default UsersListHeader;
